import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWorkspaces as apiGetWorkspaces,
  createWorkspace as apiCreateWorkspace,
  setWorkspaceAsDefault,
} from "../api";
import { useSelectionStore } from "@/state/selection.store";
import posthogAnalytics, { getWorkspaceStats } from "@/lib/posthog";
import { safeLocalStorage } from "@/lib/safe-storage";

/** Check if the current user is a platform admin (from localStorage) */
function getIsAdmin() {
  try {
    const raw = safeLocalStorage.getItem('user_data')
    if (!raw) return false
    return JSON.parse(raw).isAdmin === true
  } catch { return false }
}

export function useWorkspaces() {
  const queryClient = useQueryClient();
  const switchWorkspace = useSelectionStore(state => state.switchWorkspace);
  const activeWorkspaceId = useSelectionStore(state => state.activeWorkspaceId);
  const setWorkspaces = useSelectionStore(state => state.setWorkspaces);

  const { data, isLoading, error } = useQuery({
    queryKey: ["workspaces"],
    queryFn: apiGetWorkspaces,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Never retry auth errors — session is invalid
      if (error?.status === 401 || /401|unauthorized/i.test(error?.message))
        return false;
      return failureCount < 2;
    },
  });

  const workspaces = data?.data || [];

  // Sync workspaces to selection store for hasFeature and other global access
  useEffect(() => {
    if (workspaces.length > 0) {
      setWorkspaces(workspaces);
    }
  }, [workspaces, setWorkspaces]);

  // Update PostHog with user paid status based on workspaces
  useEffect(() => {
    if (workspaces.length > 0) {
      const stats = getWorkspaceStats(workspaces);
      posthogAnalytics.setUserProperties(stats);
    }
  }, [workspaces]);

  // Identify user + workspace traits to Mixpanel/PostHog via session tracker
  useEffect(() => {
    if (workspaces.length === 0) return;
    try {
      const raw = safeLocalStorage.getItem('user_data');
      if (!raw) return;
      const user = JSON.parse(raw);
      if (!user?.id) return;

      // Use the active workspace, fallback to default
      const activeWs = workspaces.find(ws => (ws.id || ws._id) === activeWorkspaceId)
        || workspaces.find(ws => ws.isDefault)
        || workspaces[0];

      const activePlans = activeWs?.activePlans || [];

      window.analytics?.identify(user.id, {
        email: user.email,
        name: user.name,
        workspaceId: activeWs?._id || activeWs?.id,
        workspaceName: activeWs?.name,
        isPaid: activePlans.length > 0,
        plan: activePlans[0]?.name || 'free',
      });
    } catch (e) {
      // silently ignore — analytics should never crash the app
    }
  }, [workspaces, activeWorkspaceId]);

  const defaultWorkspace = workspaces.find(ws => ws.isDefault) || workspaces[0];

  useEffect(() => {
    // Admins can access any workspace — don't override their selection
    if (getIsAdmin()) return
    if (!activeWorkspaceId && defaultWorkspace) {
      let persistedWorkspaceId = null;
      try {
        const persisted = safeLocalStorage.getItem("codedesign-selection");
        if (persisted) {
          const parsed = JSON.parse(persisted);
          persistedWorkspaceId = parsed?.state?.activeWorkspaceId || null;
        }
      } catch (e) {
        console.error("Error parsing persisted workspace ID:", e);
      }

      if (persistedWorkspaceId && workspaces.length > 0) {
        const exists = workspaces.some(ws => {
          const id = ws.id || ws._id;
          return id && String(id) === String(persistedWorkspaceId);
        });
        if (exists) {
          switchWorkspace(persistedWorkspaceId);
          return;
        }
      }

      const defaultId = defaultWorkspace._id || defaultWorkspace.id;
      if (defaultId) {
        switchWorkspace(defaultId);
      }
    }
  }, [activeWorkspaceId, defaultWorkspace, switchWorkspace, workspaces]);

  useEffect(() => {
    if (!activeWorkspaceId || !workspaces.length) return;
    // Admins can access any workspace — don't force-switch to their own
    if (getIsAdmin()) return;

    const exists = workspaces.some(ws => {
      const id = ws._id || ws.id;
      return id && String(id) === String(activeWorkspaceId);
    });

    if (!exists) {
      const fallback = workspaces.find(ws => ws.isDefault) || workspaces[0];
      const fallbackId = fallback ? fallback._id || fallback.id : null;
      switchWorkspace(fallbackId || null);
    }
  }, [activeWorkspaceId, workspaces, switchWorkspace]);

  const createMutation = useMutation({
    mutationFn: apiCreateWorkspace,
    onSuccess: resp => {
      const created = resp?.data || resp;
      queryClient.setQueryData(["workspaces"], old => {
        const prev = old?.data || old || [];
        return { data: [...prev, created] };
      });
      const newId = created?.id || created?._id;
      if (newId && (created?.isDefault || workspaces.length === 0)) {
        switchWorkspace(newId);
      }
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: workspaceId => setWorkspaceAsDefault(workspaceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  return {
    workspaces,
    loading: isLoading,
    error: error?.message || null,
    createWorkspace: name => createMutation.mutateAsync(name),
    setDefaultWorkspace: workspaceId =>
      setDefaultMutation.mutateAsync(workspaceId),
    refetchWorkspaces: () =>
      queryClient.invalidateQueries({ queryKey: ["workspaces"] }),
  };
}
